import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from 'react';
import { Path, PathValue, UseFormReturn } from "react-hook-form";
import { Database } from "@copro/api";
import { useSpace } from "../../../../hooks/useSpace";
import { keys as documentKeys } from "../../../documents/api/keys";
import { invalidatePendingQuoteViews } from "../../api/invalidatePendingQuoteViews";
import { keys } from "../../api/keys";
import { useGetDocumentsBySubjectActionId } from "../../api/useGetDocumentsBySubjectActionId";
import { Document, FormInputs, FormValues } from "./types";
import usePostActionAndDocuments from "./usePostActionAndDocuments";
import usePutActionAndDocuments from "./usePutActionAndDocuments";

export default function useSubjectActionModal<TFormValues extends FormInputs, TTransformedValues extends FormValues>(
  type: Database['public']['Enums']['action_type'],
  actionId: string | undefined,
  subjectId: string,
  form: UseFormReturn<TFormValues, unknown, TTransformedValues>,
) {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();
  const { postActionAndDocuments, isLoading: isPosting } = usePostActionAndDocuments(subjectId);
  const { putActionAndDocuments, isLoading: isUpdating } = usePutActionAndDocuments(actionId, subjectId);

  const { data: originialDocuments, isLoading: isDocumentLoading } = useGetDocumentsBySubjectActionId(actionId, subjectId, true);

  useEffect(() => {
    if (isDocumentLoading || !originialDocuments) {
      return;
    }

    const documents: Document[] = originialDocuments.map((document) => ({
      id: document.id,
      file_url: document.file_url,
      name: document.name,
      type: document.type,
      provider_id: document.provider_id,
    }));

    form.setValue("documents" as Path<TFormValues>, documents as PathValue<TFormValues, Path<TFormValues>>, { shouldDirty: false });
  }, [originialDocuments, isDocumentLoading, form]);

  async function handleConfirm(data: TTransformedValues) {
    const { documents: newDocuments, ...formData } = data;

    if (actionId) {
      await putActionAndDocuments(formData, originialDocuments ?? [], newDocuments, formData.provider_id || null);
    } else {
      await postActionAndDocuments(
        {
          subject_id: subjectId,
          type,
          date: formData.date,
          comment: formData.comment || null,
          provider_name: formData.provider_name || null,
          provider_id: formData.provider_id || null,
          contact_name: formData.contact_name || null,
          amount: formData.amount || null,
          title: formData.title || null,
          communication_type: formData.communication_type || null,
        },
        newDocuments,
        formData.provider_id || null,
      );
    }

    queryClient.invalidateQueries({ queryKey: keys.subTopic(subjectId) });
    queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });
    queryClient.invalidateQueries({ queryKey: [...keys.all, 'communications'] });
    queryClient.invalidateQueries({ queryKey: ['communications_count'] });
    queryClient.invalidateQueries({ queryKey: documentKeys.getDocumentsBySubTopicId(subjectId) });
    if (currentSpace?.id) {
      // Also refreshes the pending sub-topic cards, whatever the action type.
      invalidatePendingQuoteViews(queryClient, currentSpace.id);
    }
    if (actionId) {
      queryClient.invalidateQueries({ queryKey: keys.actionDocuments(actionId, subjectId) });
    }
  }

  return {
    postActionAndDocuments,
    putActionAndDocuments,
    isLoading: isPosting || isUpdating,
    isDocumentLoading,
    handleCallApi: handleConfirm,
  }
}
